/**
 * UndoToast and info toasts (spec 3.14): ink fill, e2 shadow, "Geri al" as the action and
 * a 2 px countdown bar that empties over the undo window.
 */

import { useEffect, useState } from 'react';
import { usePanel, useStore } from '../app/context.js';
import type { Toast } from '../app/store.js';
import { Icon } from './icons.js';

function secondsLeft(countdown: NonNullable<Toast['countdown']>): number {
  return Math.max(0, Math.ceil((countdown.startedAt + countdown.durationMs - Date.now()) / 1000));
}

function useSecondsLeft(toast: Toast): number | null {
  const countdown = toast.countdown;
  const [left, setLeft] = useState<number | null>(() => (countdown ? secondsLeft(countdown) : null));
  useEffect(() => {
    if (!countdown) return;
    const update = () => setLeft(secondsLeft(countdown));
    update();
    const timer = setInterval(update, 250);
    return () => clearInterval(timer);
  }, [countdown]);
  return left;
}

/** Only plain web links leave the panel; anything else (javascript:, data:) is shown as text. */
export function safeExternalUrl(href: string): string | null {
  try {
    const url = new URL(href);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null;
  } catch {
    return null;
  }
}

function ToastView({ toast }: { toast: Toast }) {
  const store = useStore();
  const left = useSecondsLeft(toast);
  const countdown = toast.countdown;
  const [elapsed] = useState(() => (countdown ? Math.min(countdown.durationMs, Date.now() - countdown.startedAt) : 0));
  const href = toast.link ? safeExternalUrl(toast.link.href) : null;
  return (
    <div className={`toast${toast.tone === 'danger' ? ' toast--danger' : ''}`} role="status">
      {toast.icon === 'alert' ? (
        <svg width="16" height="16" viewBox="0 0 10 10" aria-hidden="true" focusable="false" className="toast__icon">
          <path d="M5 1L9.5 9H0.5Z" fill="currentColor" />
        </svg>
      ) : (
        <Icon
          name={toast.icon === 'pause' ? 'pause' : toast.icon === 'info' ? 'history' : 'check'}
          size={16}
          stroke={2}
          className="toast__icon"
        />
      )}
      <span className="toast__message">
        {toast.message}
        {toast.link && href ? (
          <>
            {' · '}
            <a href={href} target="_blank" rel="noopener noreferrer" className="toast__link">
              {toast.link.label}
            </a>
          </>
        ) : null}
      </span>
      {left !== null ? <span className="toast__seconds mono">{left} sn</span> : null}
      {toast.action ? (
        <button
          type="button"
          className="toast__action"
          onClick={() => {
            toast.action?.run();
            // Undo closes its own toast when the executor confirms; a failed undo keeps Geri al.
            if (!toast.action?.keepOpen) store.dismissToast(toast.id);
          }}
        >
          {toast.action.label}
        </button>
      ) : null}
      {countdown ? (
        <span
          className="toast__bar"
          aria-hidden="true"
          style={{ animationDuration: `${countdown.durationMs}ms`, animationDelay: `-${elapsed}ms` }}
        />
      ) : null}
    </div>
  );
}

export function Toasts({ placement }: { placement: 'phone' | 'phone-decision' | 'desktop' }) {
  const { toasts } = usePanel();
  if (toasts.length === 0) return null;
  // Every open undo window stays reachable; of the plain notices only the latest is shown.
  const lastInfo = toasts.findLast((toast) => toast.action === null);
  const visible = toasts.filter((toast) => toast.action !== null || toast === lastInfo);
  return (
    <div className={`toasts toasts--${placement}`}>
      {visible.map((toast) => (
        <ToastView key={toast.id} toast={toast} />
      ))}
    </div>
  );
}
